import type { MorphoClientType } from '@morpho-org/morpho-sdk'

import { Time } from '@morpho-org/morpho-ts'
import { concatHex, type Hex, isHex, numberToHex, type TransactionRequest } from 'viem'

import { getEmbedderByHostname } from '@/config/embedders'
import { TransactionType } from '@/types/legacy'

export const CALLDATA_ORIGIN_SAFE = '25AFEA44'

// Origin tag for Ledger-button deposits — reconciles the per-deposit billing deal on-chain.
export const CALLDATA_ORIGIN_LEDGER = '1ED6E300'

/**
 * Calldata-origin attribution tags for a transaction. The calldata ends with
 * `[4B timestamp][4B integration?][4B base]`: `base` identifies the app and is
 * always the trailing 4 bytes, so a suffix match on it catches every tx, while
 * `integration` sits right before it only when the tx is submitted through an
 * integration, so each dimension can be queried on its own.
 */
export interface CalldataOrigin {
  base: string
  integration: string | undefined
}

/**
 * Resolves the calldata-origin attribution tags for a transaction.
 *
 * `base` is always the configured default origin. For the integration tag, a
 * Ledger-button connector's tag (the per-deposit billing deal) wins first;
 * otherwise an embedder's own tag (embedder-attributed — keyed on the framing
 * host, e.g. Mellow); otherwise a Safe account's connector-attributed tag;
 * otherwise none. Pure — the caller passes in the embedding host
 * (`getIframeEnvironment().host`), whether the wallet connector is a Safe,
 * whether it is the Ledger wallet provider, and the configured default.
 */
export function resolveCalldataOrigin({
  embedderHost,
  isSafeConnector,
  isLedgerConnector,
  defaultOrigin
}: {
  embedderHost: string | undefined
  isSafeConnector: boolean
  isLedgerConnector: boolean
  defaultOrigin: string
}): CalldataOrigin {
  const integration = isLedgerConnector
    ? CALLDATA_ORIGIN_LEDGER
    : (getEmbedderByHostname(embedderHost)?.calldataOrigin ??
      (isSafeConnector ? CALLDATA_ORIGIN_SAFE : undefined))
  return { base: defaultOrigin, integration }
}

/**
 * The origin handed to the SDK's `metadata.origin`. The SDK appends
 * `[timestamp][origin]`: with an integration that is the integration tag and the
 * the Vaults app layer appends the base after it; without one the SDK appends the base itself.
 */
export function sdkCalldataOrigin({ base, integration }: CalldataOrigin): string {
  return integration ?? base
}

/** Mirrors the SDK's origin validation: `0x`-optional, hex-only, even-length, at most 4 bytes. */
function normalizeCalldataOrigin(origin: string): Hex | undefined {
  const stripped = origin.replace(/^0x/i, '')
  if (stripped.length === 0 || stripped.length > 8 || stripped.length % 2 !== 0) return undefined
  const hex: Hex = `0x${stripped}`
  return isHex(hex) ? hex : undefined
}

/**
 * Appends an origin tag to a built transaction's calldata, after the metadata
 * the SDK already appended. Empty calldata is left untouched, as the SDK does.
 * Returns a new object; `tx` is not mutated.
 */
export function appendCalldataOrigin<T extends { readonly data: Hex }>(tx: T, origin: string): T {
  if (tx.data === '0x') return tx
  const suffix = normalizeCalldataOrigin(origin)
  if (!suffix) {
    console.warn('Invalid calldata origin, tag dropped:', origin)
    return tx
  }
  return { ...tx, data: concatHex([tx.data, suffix]) }
}

function hasData(value: unknown): value is { readonly data: Hex } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    typeof value.data === 'string' &&
    isHex(value.data)
  )
}

function isActionOutput(value: unknown): value is { buildTx: (...args: unknown[]) => unknown } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'buildTx' in value &&
    typeof value.buildTx === 'function'
  )
}

function wrapActionOutput<T extends object>(output: T, tag: string): T {
  if (!isActionOutput(output)) return output
  const { buildTx } = output
  const tagged = {
    ...output,
    buildTx: (...args: unknown[]) => {
      const tx: unknown = buildTx.apply(output, args)
      return hasData(tx) ? appendCalldataOrigin(tx, tag) : tx
    }
  }
  // oxlint-disable-next-line typescript/consistent-type-assertions
  return tagged as T
}

function wrapResult(result: unknown, tag: string): unknown {
  if (typeof result !== 'object' || result === null) return result
  if (result instanceof Promise) return result.then(resolved => wrapResult(resolved, tag))
  return wrapActionOutput(result, tag)
}

/** Proxies an SDK entity so every action output it returns (sync or async) is tagged. */
function wrapEntity<T extends object>(entity: T, tag: string): T {
  return new Proxy(entity, {
    get(target, property, receiver) {
      const value: unknown = Reflect.get(target, property, receiver)
      if (typeof value !== 'function') return value
      return (...args: unknown[]) => wrapResult(value.apply(target, args), tag)
    }
  })
}

/**
 * Keeps the base origin as the trailing tag of every tx built through the SDK's
 * `morpho` namespace. The SDK must have been configured with
 * `sdkCalldataOrigin(origin)`: with an integration it appended
 * `[timestamp][integration]`, so every `buildTx()` here appends `[base]` after it;
 * without one the SDK already appended `[timestamp][base]` and the namespace is
 * returned as-is. Requirements (approvals, signatures) are left as the SDK returns
 * them — the SDK does not tag them either.
 */
export function withIntegrationCalldataOrigin(
  morpho: MorphoClientType,
  { base, integration }: CalldataOrigin
): MorphoClientType {
  if (!integration) return morpho
  return {
    ...morpho,
    vaultV1: (vault, chainId) => wrapEntity(morpho.vaultV1(vault, chainId), base),
    vaultV2: (vault, chainId) => wrapEntity(morpho.vaultV2(vault, chainId), base),
    blue: (marketParams, chainId) => wrapEntity(morpho.blue(marketParams, chainId), base),
    midnight: chainId => wrapEntity(morpho.midnight(chainId), base)
  }
}

export const TRANSACTION_LABELS = {
  [TransactionType.MarketBorrow]: 'Borrow',
  [TransactionType.MarketLiquidation]: 'Liquidation',
  [TransactionType.MarketRepay]: 'Repay',
  [TransactionType.MarketSupply]: 'Supply',
  [TransactionType.MarketSupplyCollateral]: 'Supply collateral',
  [TransactionType.MarketWithdraw]: 'Withdraw',
  [TransactionType.MarketWithdrawCollateral]: 'Withdraw collateral',
  [TransactionType.MetaMorphoDeposit]: 'Vault Deposit',
  [TransactionType.MetaMorphoWithdraw]: 'Vault Withdraw',
  [TransactionType.MetaMorphoTransfer]: 'Vault Transfer'
} as const

type TransactionKey = keyof typeof TRANSACTION_LABELS

/**
 * Formats a transaction type into a human-readable string.
 *
 * @param {TransactionKey | string} type - The transaction type to be formatted.
 * @returns A human-readable string representing the type.
 *
 * @example
 * formatTransactionType("MetaMorphoDeposit") // returns "Supply"
 * formatTransactionType("MetaMorphoWithdraw") // returns "Withdraw"
 * @internal
 */
export const formatTransactionType = (type: TransactionKey | string): string =>
  // oxlint-disable-next-line typescript/consistent-type-assertions
  TRANSACTION_LABELS[type as TransactionKey] ?? type

/**
 * Adds debugging metadata to transaction calldata (timestamp and optional origin tag).
 * @param tx - Transaction request object
 * @param calldataOrigin - Optional origin tag (max 8 hex chars)
 * @returns Transaction with metadata appended to calldata
 * @internal
 */
export function addTransactionMetadata<T extends TransactionRequest>(
  tx: T,
  calldataOrigin?: string
): T {
  const { data, ..._tx } = tx

  if (!data) return tx

  const concatItems = [
    data,
    // add a submission date to debug reverts.
    numberToHex(Time.timestamp(), { size: 4 })
  ]

  if (calldataOrigin) {
    try {
      calldataOrigin = calldataOrigin.replace(/^(?!0x)/, '')

      if (!isHex(`0x${calldataOrigin}`))
        throw Error('Calldata origin must consists of only hex characters')
      if (calldataOrigin.length > 8)
        throw Error('Calldata origin must be at most 8 characters long')
      concatItems.push(`0x${calldataOrigin}`)
    } catch (error) {
      console.warn('Invalid calldata origin:\n', error instanceof Error ? error.name : error)
    }
  }

  // oxlint-disable-next-line typescript/consistent-type-assertions
  return { data: concatHex(concatItems), ..._tx } as T
}
